import React, { useEffect, useMemo, useState } from 'react'
import { ActionIcon, Button, Center, Group, Loader, Select, Stack, Text, Title } from '@mantine/core'
import { IconPlus, IconTrash } from '@tabler/icons-react'
import { v4 as uuid } from 'uuid';
import { useParams } from 'react-router'
import { DATA_SOURCE, useCategoryElements } from '../../../../../hooks/elements'
import { useUpdateVersionRules, useVersionRulesCache } from '../../../../../hooks/global-job-matching'
import { DebouncedTextInput } from '../../../../common';
import { numberOperators, textOperators } from '../../../../../constants/filter-operators';

type JobFilter = {
  dataSource?: number
  element: string
  operator?: string
  sourceField?: string
  type?: string
  value: string
  uuid?: string
}

export const JobsFilters = () => {
  const { data: elements, isLoading: elementsLoading } = useCategoryElements('jobs', [DATA_SOURCE.JOBS])
  const params = useParams<{ id: string }>()
  const rules = useVersionRulesCache(params.id)
  const { mutate: updateRules } = useUpdateVersionRules(params.id)
  const elementLabels =  useMemo(() => !elements ? [] : elements.map(el => el.text).sort(), [elements])
  const newFilter = { element: elementLabels.length ? elementLabels[0] : '', operator: 'like', value: '', type: 'text', uuid: uuid() } //UUID is needed for rerendering
  const [filters, setFilters] = useState<JobFilter[]>([])

  const validateAndUpdateFilters = (updatedFilters: JobFilter[]) => {
    const validFilters = updatedFilters.map(filter => {
      const filterElement = elements?.find(el => el.text === filter.element)
      return {
        element: filter.element,
        operator: filter.operator,
        value: filter.type === 'number' ? Number(filter.value) : filter.value,
        dataSource: filterElement?.nonaggregate_data_sources[0],
        sourceField: filterElement?.source_field,
        type: filter.type
      }
    })
    updateRules({ jobsFilter: validFilters })
  }

  const handleFilterChange = (filterKey: 'operator' | 'value', value: string, filterIndex: number) => {
    const updatedFilters = filters.map((f, i) => (filterIndex === i) ? ({ ...f, [filterKey]: value }) : f)
    setFilters(updatedFilters)
    validateAndUpdateFilters(updatedFilters)
  }

  const handleElementChange = (value: string, filterIndex: number) => {
    if (filters[filterIndex].element === value) return
    const fieldType = elements?.find(el => el.text === value)?.field_type_code === 'text' ? 'text' : 'number' // JOB ID has field_type_code of primary_key but needs to be treated as number. This may need to be changed in the future to support more field_type_codes
    const updatedFilters = filters.map((f, i) => {
      if (filterIndex === i) {
        const isSameFieldType = fieldType === f.type
        return {
          ...f,
          element: value,
          value: (isSameFieldType || fieldType === 'text' || (fieldType === 'number' && !isNaN(Number(f.value)))) ? f.value : '0',
          type: fieldType || 'text',
          operator: isSameFieldType ? f.operator : fieldType === 'text' ? textOperators[0].value : numberOperators[0].value
        }
      } else {
        return f
      }
    })
    validateAndUpdateFilters(updatedFilters)
  }

  const handleDelete = (uuid: string) => {
    const updatedFilters = filters.filter((f) => f.uuid !== uuid)
    setFilters(updatedFilters)
    validateAndUpdateFilters(updatedFilters)
  }

  useEffect(() => {
    if (rules?.jobsFilter?.length) {
      const filtersWithId = rules.jobsFilter.map(f => ({ ...f, uuid: uuid() }))
      setFilters(filtersWithId)
    } else {
      setFilters([newFilter])
    }
  }, [rules, elementLabels])

  if (rules?.jobsSelection !== 'select') return

  return (
    <Stack>
      <Title order={2} style={{ fontWeight: 600 }}>Let's select the jobs</Title>
      <Text size='lg'>Pick specific jobs to tailor this rule</Text>
      { elementsLoading ?
        <Center mt='xl'><Loader size='xl' /></Center> :
        <>
          <Text c='darkGrey' style={{ fontSize: 18 }}>Filter</Text>
          { filters.map((f, idx) => (
            <Group key={f.uuid} align='flex-end' wrap='nowrap'>
              <Select
                flex={2}
                size='lg'
                value={f.element}
                placeholder='Select Field'
                data={elementLabels}
                allowDeselect={false}
                onChange={(value) => handleElementChange(value as string, idx)}
              />
              <Select
                flex={1}
                size='lg'
                data={f.type === 'text' ? textOperators : numberOperators}
                value={f.operator}
                allowDeselect={false}
                onChange={value => handleFilterChange('operator', value as string, idx)}
              />
              <Group flex={2} wrap='nowrap'>
                <DebouncedTextInput
                  size='lg'
                  w='100%'
                  placeholder='Search within this field'
                  disabled={f.operator === 'isNull' || f.operator === 'isNotNull'}
                  value={f.value}
                  onDebounceChange={val => handleFilterChange('value', val, idx)}
                  numbersOnly={f.type === 'number'}
                />
                { idx !== 0 &&
                <ActionIcon radius='50%' size={50} color='blueGrey' c='darkGrey' onClick={() => handleDelete(f.uuid as string)}>
                  <IconTrash stroke={2} size={20} />
                </ActionIcon>
                }
              </Group>
            </Group>
          ))}
          <Button
            w='100%'
            size='lg'
            color='blueGrey'
            c='black'
            style={{ fontSize: 16, fontWeight: 500 }}
            leftSection={<IconPlus stroke={2} size='16' />}
            onClick={() => setFilters(f => [...f, newFilter])}
          >
            Add Additional Filter
          </Button>
        </>

      }
    </Stack>
  )
}
